import { useEffect, useMemo, useState } from 'react';
import Carregando from '@/components/Carregando';
import { useAuth } from '@/auth/AuthProvider';
import { useLeads } from '@/leads/LeadsProvider';
import { temFuncao } from '@/equipe/funcoes';
import { MODELOS, calcularPessoa } from '@/remuneracao/calculo';
import { ouvirConfigs, removerConfig, salvarConfig } from '@/services/remuneracaoService';

/**
 * Configuração da remuneração, por pessoa (VC) — só o gestor.
 *
 * ★ MOSTRA O EFEITO EM REAIS ENQUANTO SE DIGITA.
 *
 * Digitar `7` no lugar de `0,7` é um erro de dez vezes no pagamento de alguém, e
 * um limite rígido não serve: o percentual é livre por definição — a clínica
 * pode combinar 3 % com uma pessoa. O que pega o erro é **ver o número**: "0,7%
 * sobre R$ 12.000 no mês = R$ 84,00" e "7% = R$ 840,00" são frases que ninguém
 * confunde, enquanto `0.7` e `7` num campo são quase o mesmo desenho.
 */

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Texto brasileiro ("1.250,50") → número. O ponto é milhar; a vírgula, decimal. */
const numero = (v) => {
  const n = Number(String(v ?? '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/**
 * ★ Número guardado → texto do campo, em notação BRASILEIRA.
 *
 * `String(0.7)` é `"0.7"`, e o `numero` acima — que lê à brasileira — enxerga o
 * ponto como separador de milhar: `"0.7"` vira **7**. Semear o campo com
 * `String()` cru fazia um percentual de 0,7 % aparecer e ser regravado como
 * 7 % — dez vezes o pagamento de alguém, exatamente o erro que o efeito ao vivo
 * desta tela existe para pegar.
 *
 * Descoberto pelo teste que MONTA a tela: nenhum teste de lógica pura pegaria,
 * porque o defeito mora na ida e volta entre o número e o campo de texto.
 */
const paraCampo = (v) =>
  v === null || v === undefined || v === '' ? '' : String(v).replace('.', ',');

/** Cenário do exemplo. Redondo de propósito: serve para comparar, não para prever. */
const EXEMPLO = { comparecimentos: 30, recebido: 12000 };

function Linha({ pessoa, config, aoSalvar, aoRemover, ocupado }) {
  const [modelo, setModelo] = useState(config?.modelo ?? 'fixo_percentual');
  const [fixo, setFixo] = useState(paraCampo(config?.valorPorComparecimento));
  const [percentual, setPercentual] = useState(paraCampo(config?.percentual));
  const [salvo, setSalvo] = useState(false);

  // Trocar de config recarrega os campos: um valor pendurado da linha anterior
  // seria salvo na pessoa errada.
  useEffect(() => {
    setModelo(config?.modelo ?? 'fixo_percentual');
    setFixo(paraCampo(config?.valorPorComparecimento));
    setPercentual(paraCampo(config?.percentual));
    setSalvo(false);
  }, [pessoa.id, config?.modelo, config?.valorPorComparecimento, config?.percentual]);

  const previa = useMemo(
    () =>
      calcularPessoa(
        { modelo, valorPorComparecimento: numero(fixo), percentual: numero(percentual) },
        { comparecimentos: EXEMPLO.comparecimentos, baseRecebida: EXEMPLO.recebido },
      ),
    [modelo, fixo, percentual],
  );

  const mudou =
    modelo !== (config?.modelo ?? 'fixo_percentual')
    || numero(fixo) !== Number(config?.valorPorComparecimento ?? 0)
    || numero(percentual) !== Number(config?.percentual ?? 0);

  return (
    <div className="cartao">
      <div className="linha">
        <div className="campo">
          <label htmlFor={`m-${pessoa.id}`}>{pessoa.nome}</label>
          <select id={`m-${pessoa.id}`} value={modelo} disabled={ocupado} onChange={(e) => setModelo(e.target.value)}>
            {MODELOS.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor={`f-${pessoa.id}`}>Por comparecimento (R$)</label>
          <input
            id={`f-${pessoa.id}`}
            inputMode="decimal"
            placeholder="2,00"
            value={fixo}
            disabled={ocupado}
            onChange={(e) => { setFixo(e.target.value); setSalvo(false); }}
          />
        </div>
        <div className="campo">
          <label htmlFor={`p-${pessoa.id}`}>Percentual do recebido (%)</label>
          <input
            id={`p-${pessoa.id}`}
            inputMode="decimal"
            placeholder="0,7"
            value={percentual}
            disabled={ocupado || modelo === 'fixo'}
            onChange={(e) => { setPercentual(e.target.value); setSalvo(false); }}
          />
        </div>
      </div>

      {/* ★ O efeito em reais, atualizado a cada tecla. */}
      <p className="campo-ajuda">
        Num mês com <strong>{EXEMPLO.comparecimentos} comparecimentos</strong> e{' '}
        <strong>{moeda(EXEMPLO.recebido)}</strong> recebidos:{' '}
        {moeda(previa.valorFixo)} de fixo
        {modelo === 'fixo_percentual' && (
          <>
            {' + '}
            <strong>
              {String(numero(percentual)).replace('.', ',')}% de {moeda(EXEMPLO.recebido)} ={' '}
              {moeda(previa.comissao)}
            </strong>
          </>
        )}
        {' = '}
        <strong className="valor">{moeda(previa.total)}</strong>
      </p>

      <footer className="dialogo-acoes">
        {salvo && !mudou && <span className="campo-ajuda">Salvo.</span>}
        {/*
          ★★ WJ — "Remover da folha": o único caminho de SAÍDA.

          TRAVA 1 — só aparece com config. Sem documento não há o que remover, e
          um botão inerte em toda linha convidaria ao clique.

          TRAVA 2 — a confirmação NOMEIA a pessoa. É o que faz alguém perceber que
          está na linha errada; é o mesmo padrão do fechamento do mês
          (`RemuneracaoPage.jsx:247`), que também põe o que está em jogo no texto.
          ⚠ Não exijo digitar o nome nem senha: seria mais cerimônia do que fechar
          a folha, que move dinheiro de verdade — e desproporção vira clique no
          automático.

          TRAVA 3 — `disabled` enquanto ocupado, como o Salvar.

          ★ O dano é reversível: a config são três campos, e os da Sorridents são
          iguais para todo mundo. O que não volta é a trilha de `atualizadoPor`.
        */}
        {config && (
          <button
            type="button"
            className="btn-secundario"
            disabled={ocupado}
            onClick={async () => {
              if (!window.confirm(
                `Remover ${pessoa.nome || 'esta pessoa'} da folha? `
                + 'Ela deixa de ser calculada nos próximos fechamentos. '
                + 'O que já foi fechado não muda.',
              )) return;
              await aoRemover();
              setSalvo(false);
            }}
          >
            Remover da folha
          </button>
        )}
        <button
          type="button"
          className="btn-primario"
          disabled={ocupado || !mudou}
          onClick={async () => {
            await aoSalvar({ modelo, valorPorComparecimento: numero(fixo), percentual: numero(percentual) });
            setSalvo(true);
          }}
        >
          Salvar
        </button>
      </footer>
    </div>
  );
}

export default function ConfigRemuneracaoPage() {
  const { clinicaId, usuario } = useAuth();
  const { equipe } = useLeads();
  const [configs, setConfigs] = useState(null);
  const [erro, setErro] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirConfigs(clinicaId, setConfigs, (e) => {
      console.error('[OdontoSDR] Falha ao ouvir remuneração:', e);
      setErro('Não foi possível carregar as configurações.');
    });
  }, [clinicaId]);

  /**
   * ★★ WJ (29/set/2026) — SÓ QUEM EXERCE A FUNÇÃO DE SDR.
   *
   * Era `ehVendedor`, que é `sdr || negociador` (`metas/credito.js:47`). Medido:
   * isso punha o JOABE — acesso de vendas, função SÓ negociador — nesta lista, e
   * **um clique em Salvar bastava para ele entrar na folha**, porque a folha é a
   * lista de quem tem config (`remuneracao/calculo.js:229`). Não era teoria: ele
   * estava na tela.
   *
   * ★ A troca é no PONTO DE USO, nunca na definição. `ehVendedor` e `vendedores`
   * continuam aceitando negociador — é o que mantém o placar e as metas
   * inteiros. Mexer na definição tiraria o negociador de lá junto, sem ninguém
   * pedir. (Mutação M5.)
   */
  const pessoas = useMemo(() => (equipe ?? []).filter((u) => temFuncao(u, 'sdr')), [equipe]);

  if (erro) return <p className="erro">{erro}</p>;
  if (!configs) return <Carregando />;

  async function salvar(uid, dados) {
    setErro(null);
    setOcupado(true);
    try {
      await salvarConfig(clinicaId, uid, dados, { id: usuario?.uid, nome: usuario?.displayName ?? '' });
    } catch (e) {
      setErro(e.message ?? 'Não foi possível salvar.');
    } finally {
      setOcupado(false);
    }
  }

  async function remover(uid) {
    setErro(null);
    setOcupado(true);
    try {
      await removerConfig(clinicaId, uid);
    } catch (e) {
      setErro(e.message ?? 'Não foi possível remover da folha.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section className="pagina">
      <header className="pagina-cabecalho">
        <h1>Remuneração da equipe</h1>
      </header>

      <p className="secao-sub">
        O percentual é sobre o que o paciente <strong>efetivamente pagou</strong> no mês, não
        sobre o valor fechado — parcela que entra em novembro é comissão de novembro, mesmo
        que o contrato seja de março. Quem não tiver configuração aqui{' '}
        <strong>não aparece na folha</strong>.
      </p>

      {pessoas.map((p) => (
        <Linha
          key={p.id}
          pessoa={p}
          config={configs[p.id] ?? null}
          ocupado={ocupado}
          aoSalvar={(dados) => salvar(p.id, dados)}
          aoRemover={() => remover(p.id)}
        />
      ))}
    </section>
  );
}
